import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from './utils';
import { Home, Users, User, Shield, ChefHat, Gift, Stethoscope, Building2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import NotificationBell from '@/components/notifications/NotificationBell';

export default function Layout({ children, currentPageName }) {
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
        setIsAdmin(currentUser.role === 'admin');
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const navItems = [
    { name: 'Home', label: 'Inicio', icon: Home },
    { name: 'Community', label: 'Comunidad', icon: Users },
    { name: 'Profile', label: 'Perfil', icon: User },
  ];

  const additionalNavItems = [];
  if (user?.is_professional) {
    additionalNavItems.push({ name: 'ProfessionalDashboard', label: 'Profesional', icon: Stethoscope });
  }
  
  // Check if user has manufacturer access
  const [hasManufacturer, setHasManufacturer] = React.useState(false);
  React.useEffect(() => {
    if (user?.id) {
      base44.entities.Manufacturer.filter({ claimed_by_user_id: user.id })
        .then(result => setHasManufacturer(result.length > 0))
        .catch(() => setHasManufacturer(false));
    }
  }, [user?.id]);

  if (hasManufacturer) {
    additionalNavItems.push({ name: 'ManufacturerDashboard', label: 'Fabricante', icon: Building2 });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Bell - Fixed top right, respecting status bar */}
      {user && (
        <div className="fixed right-4 z-40" style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
          <NotificationBell user={user} />
        </div>
      )}

      {children}

      {/* Bottom Navigation - respects home indicator */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="max-w-lg mx-auto px-2 sm:px-4">
          <div className={`flex items-center ${isAdmin || additionalNavItems.length > 0 ? 'justify-between' : 'justify-around'} py-1 sm:py-2`}>
            {isAdmin && (
              <Link
                to={createPageUrl('Admin')}
                className={`
                  flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl transition-all min-w-[3rem]
                  ${currentPageName === 'Admin'
                    ? 'text-indigo-600' 
                    : 'text-gray-400 hover:text-gray-600'
                  }
                `}
              >
                <Shield className={`w-6 h-6 ${currentPageName === 'Admin' ? 'stroke-[2.5]' : ''}`} />
                <span className={`text-xs font-medium ${currentPageName === 'Admin' ? 'font-semibold' : ''}`}>
                  Admin
                </span>
                {currentPageName === 'Admin' && (
                  <div className="absolute bottom-0 w-12 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-full" />
                )}
              </Link>
            )}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPageName === item.name;
              
              return (
                <Link
                  key={item.name}
                  to={createPageUrl(item.name)}
                  className={`
                    flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl transition-all min-w-[3rem]
                    ${isActive 
                      ? 'text-emerald-600' 
                      : 'text-gray-400 hover:text-gray-600'
                    }
                  `}
                >
                  <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5]' : ''}`} />
                  <span className={`text-xs font-medium ${isActive ? 'font-semibold' : ''}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <div className="absolute bottom-0 w-12 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-full" />
                  )}
                </Link>
              );
            })}
            {additionalNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPageName === item.name;
              
              return (
                <Link
                  key={item.name}
                  to={createPageUrl(item.name)}
                  className={`
                    flex flex-col items-center gap-0.5 py-1.5 px-2 rounded-xl transition-all min-w-[2.5rem]
                    ${isActive 
                      ? 'text-blue-600' 
                      : 'text-gray-400 hover:text-gray-600'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                  <span className={`text-[10px] font-medium ${isActive ? 'font-semibold' : ''}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <div className="absolute bottom-0 w-10 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-full" />
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Bottom padding: nav height + safe area */}
      <div style={{ height: 'calc(3.5rem + env(safe-area-inset-bottom, 0px))' }} />
    </div>
  );
}